import React, { useEffect, useMemo, useState } from 'react';
import { FilterSideBar, useDebounce } from '@ole-ui/ole-ui-components';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import useMobxStores from 'hooks/useMobxStores';
import ComponentError from 'components/Error/ComponentError';
import ExploreSubscriptions from 'components/ExploreSubscriptions';
import { handleError } from 'services/ErrorService';
import {
  TextInput,
  Text,
  TextVariants,
  FormGroup,
  MenuToggle,
  Select,
  SelectList,
  SelectOption,
  MenuToggleElement,
} from '@patternfly/react-core';

import './styles.scss';
import { useTranslation } from 'react-i18next';
import { SortingType } from 'stores/Catalog/types';

type Props = {
  title?: string;
  onFilterSelect?: (groupKey: string, option: string) => void;
  onSearch?: (query: string) => void;
  children?: React.ReactNode;
};

const CatalogFilters = ({
  title,
  onFilterSelect,
  onSearch,
  children,
}: Props) => {
  const { t } = useTranslation();
  const { catalogStore, searchStore, routerStore } = useMobxStores();
  const filterConfigs = catalogStore.getFilterConfigs();
  const debounceRef = React.useRef<number | null>(0);

  const [searchSubstring, setSearchSubstring] = useState(
    (routerStore.route as any)?.params?.q || '',
  );
  const debouncedSearchSubstring = useDebounce<string>(searchSubstring, 300);

  // sorting state
  const [isOpen, setIsOpen] = React.useState(false);

  const filterGroups = React.useMemo(
    () =>
      filterConfigs.map((filterConfig) => ({
        title: filterConfig.title,
        groupKey: filterConfig.key,
        'data-analytics-id': `${filterConfig.key}-filt-cat-lp`,
        hasError: Boolean(catalogStore.filterInfoErrors?.[filterConfig.key]),
        options:
          catalogStore.filters[filterConfig.key]?.map((option) => {
            const optionAnalyticsId = `${option.key}-${filterConfig.key}-filt-cat-lp`;
            return {
              ...option,
              title: option.value,
              navItemProps: {
                'data-analytics-id': `container-${optionAnalyticsId}`,
              },
              checkboxProps: {
                'data-analytics-id': optionAnalyticsId,
              },
            };
          }) || [],
        mergeIdenticalLabels: filterConfig.groupIdenticalLabels,
      })),
    [filterConfigs, catalogStore.filters, catalogStore.filterInfoErrors],
  );

  const allItemsFailed =
    filterGroups.length > 0 && filterGroups.every((fg) => fg.hasError);

  const handleFilterSelect = (groupKey: string, option: string) => {
    catalogStore.toggleFilterOption(groupKey, option);
    onFilterSelect?.(groupKey, option);
    catalogStore.recalculateFilterCounts();
  };

  const onToggleClick = () => {
    setIsOpen(!isOpen);
  };

  const onSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string,
  ) => {
    if (catalogStore.sortBy !== value) {
      catalogStore.updateSorting(value as SortingType);
    }

    setIsOpen(false);
  };

  const sortingLabels = useMemo(() => {
    const labelMap: Record<
      SortingType,
      { label: string; analyticsId: string }
    > = {
      [SortingType.Alphabetical]: {
        label: t('Title-alphabetical'),
        analyticsId: t('title-alphabetical'),
      },
      [SortingType.RevAlphabetical]: {
        label: t('Title-rev-alphabetical'),
        analyticsId: t('title-rev-alphabetical'),
      },
      [SortingType.Newest]: {
        label: t('Newest'),
        analyticsId: t('newest'),
      },
      [SortingType.Oldest]: {
        label: t('Oldest'),
        analyticsId: t('oldest'),
      },
      [SortingType.SKUAlphabetical]: {
        label: catalogStore.sortBy,
        analyticsId: catalogStore.sortBy,
      },
      [SortingType.SKURevAlphabetical]: {
        label: catalogStore.sortBy,
        analyticsId: catalogStore.sortBy,
      },
    };

    return (
      labelMap[catalogStore.sortBy] || {
        label: catalogStore.sortBy,
        analyticsId: catalogStore.sortBy,
      }
    );
  }, [catalogStore.sortBy, t]);

  const sortingLabel = sortingLabels.label;
  const sortingLabelAnalyticsId = sortingLabels.analyticsId;

  const toggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onToggleClick}
      isExpanded={isOpen}
      isFullWidth
      className="catalog-filters__sorting-toggle"
      data-analytics-id={`${sortingLabelAnalyticsId}-dropdown-option-catalog-view-lp`}
    >
      {t('Sort by')} - {sortingLabel}
    </MenuToggle>
  );

  useEffect(() => {
    const trimmed = debouncedSearchSubstring?.trim?.();
    searchStore.query = trimmed || ''; // update search query

    if (routerStore.route) {
      const routeParams = (routerStore.route as any)?.params || {};
      routerStore.navigate(
        routerStore.route.name,
        {
          ...routeParams,
          q: trimmed || undefined,
        },
        // @ts-ignore // this is a bug in the types
        { replace: true },
      ); // update query param
    }

    if (trimmed !== '') {
      catalogStore.filterEntries(); // filter entries
    }

    if (debounceRef.current > 0) {
      onSearch?.(trimmed);
    }
    debounceRef.current += 1;
  }, [debouncedSearchSubstring]);

  const handleSearch = (_event: unknown, value: string) => {
    setSearchSubstring(value);
  };

  useEffect(() => {
    if ((routerStore.route as any)?.params?.q) {
      handleSearch(null, (routerStore.route as any).params.q);
    }
  }, [(routerStore.route as any)?.params?.q]);

  return (
    <FilterSideBar
      expandedGroups={catalogStore.expandedFilterGroups}
      onGroupExpand={catalogStore.toggleFilterGroups}
      onFilterSelect={handleFilterSelect}
      aria-label={t('Filter the list of offerings')}
      className="catalog-filters"
      allItemsFailed={allItemsFailed}
    >
      <div className="catalog-filters__header">
        <Text className="catalog-filters__title" component={TextVariants.h3}>
          {title || t('Catalog')}
        </Text>
        <ExploreSubscriptions className="catalog-filters__howtobuy" />
      </div>
      <FilterSideBar.NavGroup title={t('Filters')}>
        <FilterSideBar.ActiveFilterLabels groups={filterGroups} numLabels={5} />
        <div className="catalog-filters__search">
          <FormGroup label={t('Search')} type="string">
            <TextInput
              className="catalog-filters__search-input"
              data-analytics-id="text-filter-filt-cat-lp"
              value={searchSubstring}
              placeholder={t('Search offering titles')}
              onChange={handleSearch}
              aria-label={t('Search offerings')}
              maxLength={50}
              onFocus={(e) => {
                e.target?.select?.();
              }}
            />
          </FormGroup>
        </div>
        <div
          className="catalog-filters__sorting"
          data-analytics-id="sorting-dropdown-catalog-view-lp"
        >
          <Select
            id="sorting-select"
            isOpen={isOpen}
            selected={catalogStore.sortBy}
            onSelect={onSelect}
            onOpenChange={(open) => setIsOpen(open)}
            toggle={toggle}
            shouldFocusToggleOnSelect
          >
            <SelectList>
              {Object.entries(SortingType).map(([key, value], index, arr) => (
                <React.Fragment key={key}>
                  <SelectOption value={value}>{value}</SelectOption>
                  {index !== arr?.length - 1 ? <hr /> : null}
                </React.Fragment>
              ))}
            </SelectList>
          </Select>
        </div>
        {filterGroups.map((fg) => (
          <FilterSideBar.FilterGroup key={fg.groupKey} {...fg} />
        ))}
      </FilterSideBar.NavGroup>
      {children}
    </FilterSideBar>
  );
};

export default withErrorBoundary(
  observer(CatalogFilters),
  ComponentError,
  handleError,
);
